import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomDialog from 'components/Dialog/Dialog';
import { RHFTextFieldOutlined } from 'components/RHF';
import * as yup from 'yup';
import { useAddManagerReconciliationNote } from '../../hooks/mutations/useReconciliationMutations';
import moment from 'moment';

interface AddNoteProps {
    open: boolean;
    onClose: () => void;
    onSubmit?: (data: { note: string }) => void;
    storeId?: string;
    date?: Date | null;
    existingNote?: string;
    isViewMode?: boolean;
    isVerified?: boolean;
    isAck?: boolean;
    cashierShiftNotes?: Array<Record<string, any>>;
}

interface AddNoteFormValues {
    note: string;
}

const addNoteSchema = yup.object().shape({
    note: yup.string().required('Note is required')
});

const AddNote: React.FC<AddNoteProps> = ({
    open,
    onClose,
    onSubmit,
    storeId,
    date,
    existingNote = '',
    isViewMode = false,
    isVerified = false,
    isAck = false,
    cashierShiftNotes = []
}) => {
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const addManagerNoteMutation = useAddManagerReconciliationNote(storeId || '');

    const methods = useForm<AddNoteFormValues>({
        resolver: yupResolver(addNoteSchema),
        defaultValues: {
            note: ''
        },
        mode: 'onChange'
    });

    const { handleSubmit, reset } = methods;

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (open) {
            reset({
                note: ''
            });
            setError(null);
        } else {
            reset({
                note: ''
            });
            setError(null);
        }
    }, [open, reset]);

    const handleFormSubmit = useCallback(async (data: AddNoteFormValues) => {
        try {
            setError(null);
            setIsSubmitting(true);

            const payload = {
                reconciliation_date: date ? moment(date).format('YYYY-MM-DD') : '',
                note: data.note
            };

            const result = await addManagerNoteMutation.mutateAsync(payload) as any;

            if (result && result.success) {
                // Call custom onSubmit if provided (for backward compatibility)
                if (onSubmit) {
                    await onSubmit(data);
                }
                // Reset form and close
                reset({
                    note: ''
                });
                onClose();
            } else {
                setError(result?.error || 'Error submitting note');
            }
        } catch (err: any) {
            console.error('Error submitting note:', err);
            setError(err.message || 'Error submitting note');
        } finally {
            setIsSubmitting(false);
        }
    }, [onSubmit, onClose, reset, date, addManagerNoteMutation]);

    return (
        <CustomDialog
            open={open}
            handleClose={onClose}
            handleSubmit={handleSubmit(handleFormSubmit)}
            title={isViewMode ? "View/Add Manager Note" : "Add Manager Note"}
            buttonTxt="Save"
            cancelBtn={true}
            cancelBtnTxt="Cancel"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            sx={{
                "& .MuiPaper-root": {
                    minWidth: "580px",
                }
            }}
        >
            <FormProvider {...methods}>
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {error && (
                        <Typography sx={{ color: 'error.main', fontSize: '14px' }}>
                            {error}
                        </Typography>
                    )}
                    <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {existingNote ? (
                            <Box sx={{ p: 1.5, bgcolor: '#F6F8FB', border: '1px solid #E4E8F0', borderRadius: 1 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#4F5E72', mb: 0.5 }}>
                                    Existing Manager Note
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: '#1f2937', whiteSpace: 'pre-wrap' }}>
                                    {existingNote}
                                </Typography>
                            </Box>
                        ) : null}
                        {cashierShiftNotes.length > 0 ? (
                            <Box sx={{ p: 1.5, bgcolor: '#F6F8FB', border: '1px solid #E4E8F0', borderRadius: 1 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#4F5E72', mb: 0.5 }}>
                                    Cashier Shift Notes
                                </Typography>
                                {cashierShiftNotes.map((shiftNote, index) => (
                                    <Typography
                                        key={String(shiftNote.id || shiftNote.created_at || `${shiftNote.note || shiftNote.text || shiftNote.message}-${index}`)}
                                        sx={{ fontSize: '13px', color: '#1f2937', whiteSpace: 'pre-wrap', mb: index === cashierShiftNotes.length - 1 ? 0 : 0.75 }}
                                    >
                                        {String(shiftNote.note || shiftNote.text || shiftNote.message || '')}
                                    </Typography>
                                ))}
                            </Box>
                        ) : null}
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontWeight: 600,
                                fontSize: '16px',
                                color: '#1b3e61'
                            }}
                        >
                            Manager Note
                        </Typography>
                        <RHFTextFieldOutlined
                            name="note"
                            placeholder="Enter the operational note for this mismatch..."
                            multiline
                            minRows={5}
                            fullWidth
                            disabled={isSubmitting}
                            sx={{
                                '& .MuiInputBase-root': {
                                    padding: '8px !important',
                                }
                            }}
                        />
                    </Box>
                </Box>
            </FormProvider>
        </CustomDialog>
    );
};

export default AddNote;
